import { createSlice } from '@reduxjs/toolkit';
import { getDefaultSidebarWidth } from '../utils/defaultSidebarWidth';

const followedArtistsSlice = createSlice({
    name: 'followedArtists',
    initialState: {
        artists: [],
        followedSet: {}, // artistName -> true for quick lookup
        // artistName -> true when the user turned the notification bell on for
        // that artist. Separate from followedSet: following alone never
        // subscribes to notifications (bell model, Aug 2026).
        notifySet: {},
        total: 0,
        // True once the follow count has been fetched at least once this
        // session — see the matching `loaded` note in likesSlice. The feed
        // personalize nudge waits on this so it doesn't fire on a stale 0.
        loaded: false,
        loading: false,
        sidebarOpen: false,
        sidebarWidth: getDefaultSidebarWidth(),
        error: null,
    },
    reducers: {
        setFollowedArtists(state, action) {
            state.artists = action.payload.data;
            state.total = action.payload.total;
            state.loaded = true;
        },
        addFollow(state, action) {
            const artist = action.payload;
            state.artists = [artist, ...state.artists];
            state.total += 1;
            state.followedSet[artist.artist_name] = true;
        },
        removeFollow(state, action) {
            const { artistName } = action.payload;
            state.artists = state.artists.filter(a => a.artist_name !== artistName);
            state.total = Math.max(0, state.total - 1);
            delete state.followedSet[artistName];
            delete state.notifySet[artistName];
        },
        updateFollowedSet(state, action) {
            Object.assign(state.followedSet, action.payload);
        },
        updateNotifySet(state, action) {
            for (const [name, on] of Object.entries(action.payload)) {
                if (on) state.notifySet[name] = true;
                else delete state.notifySet[name];
            }
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setFollowedArtistsSidebarOpen(state, action) {
            state.sidebarOpen = action.payload;
        },
        toggleFollowedArtistsSidebar(state) {
            state.sidebarOpen = !state.sidebarOpen;
        },
        setFollowedArtistsSidebarWidth(state, action) {
            state.sidebarWidth = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
        },
        clearFollowedArtists(state) {
            state.artists = [];
            state.followedSet = {};
            state.notifySet = {};
            state.total = 0;
            state.loaded = false;
            state.loading = false;
            state.sidebarOpen = false;
            state.error = null;
        },
    },
});

export const {
    setFollowedArtists,
    addFollow,
    removeFollow,
    updateFollowedSet,
    updateNotifySet,
    setLoading,
    setFollowedArtistsSidebarOpen,
    toggleFollowedArtistsSidebar,
    setFollowedArtistsSidebarWidth,
    setError,
    clearFollowedArtists,
} = followedArtistsSlice.actions;

export default followedArtistsSlice.reducer;
